import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { StarMark } from "@/components/Logo";
import { STAR_PURPLE_DATA_URI } from "@/components/brandStar";
import { DemoLoopSlot } from "@/components/DemoLoopSlot";
import { Seo } from "@/components/Seo";
import { IpProtectedStrip } from "@/components/PatentFiledBadge";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { EkselensSsoButton } from "@/components/EkselensSsoButton";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { Scale, ShieldCheck, Lock, Network } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function Login() {
  const { t } = useTranslation();
  const { user, login, verifyMfaLogin, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // Demo-account labels translate per-locale; emails stay verbatim.
  const DEMO_ACCOUNTS = [
    { email: "admin@erio.test", role: t("login.demoRoleAdmin") },
    { email: "cro@erio.test", role: t("login.demoRoleCro") },
    { email: "ciso@erio.test", role: t("login.demoRoleCiso") },
    { email: "cpo@erio.test", role: t("login.demoRoleCpo") },
    { email: "cfo@erio.test", role: t("login.demoRoleCfo") },
  ];

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api
      .get("/auth/oidc/config-status")
      .then(({ data }) => setSsoEnabled(Boolean(data?.enabled)))
      .catch(() => setSsoEnabled(false));
    const params = new URLSearchParams(window.location.search);
    const ssoErr = params.get("sso_error");
    if (ssoErr === "not_configured") {
      toast.message(t("login.ssoNotConfiguredToast"), { duration: 6000 });
    } else if (ssoErr) {
      toast.error(t("login.ssoErrorToast"));
    }
    let signedOutFlag = false;
    try { signedOutFlag = sessionStorage.getItem("erio_signed_out") === "1"; } catch (_e) { /* private mode */ }
    if (params.get("signed_out") || signedOutFlag) {
      toast.success(t("login.signedOutToast"), { duration: 4000 });
      try { sessionStorage.removeItem("erio_signed_out"); } catch (_e) { /* private mode */ }
    }
    // Session-expired: rendered as a warning (not success) so the user
    // understands their token timed out vs. an explicit sign-out.
    // Surfaced via querystring AND sessionStorage breadcrumb (the
    // breadcrumb route is hit when AuthContext bootstrap detects a
    // stale token and ProtectedRoute bounces here without a query).
    let sessionExpiredFlag = false;
    try { sessionExpiredFlag = sessionStorage.getItem("erio_session_expired") === "1"; } catch (_e) { /* private mode */ }
    if (params.get("session_expired") || sessionExpiredFlag) {
      toast.warning(t("login.sessionTimedOutToast"), { duration: 6000 });
      try { sessionStorage.removeItem("erio_session_expired"); } catch (_e) { /* private mode */ }
    }
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.mfaRequired) {
        setMfaToken(res.mfaToken);
        setLoading(false);
        return;
      }
      // Resume-where-you-left-off after session-expiry. Path was stashed
      // by the 401 interceptor before the redirect to /login.
      let next = null;
      try { next = sessionStorage.getItem("erio_post_login_redirect"); } catch (_e) { /* private mode */ }
      if (next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/login")) {
        try { sessionStorage.removeItem("erio_post_login_redirect"); } catch (_e) { /* private mode */ }
        navigate(next, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyMfaLogin(mfaToken, mfaCode.trim());
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-ek-canvas lg:grid-cols-2">
      <Seo
        title={t("login.seoTitle")}
        description={t("login.seoDescription")}
        path="/login"
        localized
      />
      {/* Left — brand panel (matches ekselens.com hero: dark purple gradient
          with the oversized cropped Ekselens star at low opacity on the right) */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-[#1a0a3a] via-[#2c0f5e] to-[#0b0b0c] p-12 lg:flex">
        {/* Oversized cropped star watermark — reuses the canonical SVG `StarMark`
            so it always renders (no asset-routing dependency). Cropped off the
            right edge exactly like the ekselens.com hero. */}
        <StarMark
          size={1100}
          className="pointer-events-none absolute -right-[440px] -top-[180px] text-ek-purple/25 select-none"
        />
        <div className="relative flex items-center gap-3">
          <StarMark size={32} className="text-white" />
          <span className="font-display text-xl font-semibold tracking-[-0.02em] text-white">
            EKSELENS <span className="font-light text-white/55">· ERIO</span>
          </span>
        </div>
        <div className="relative max-w-md">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ek-lilac">
            {t("login.heroEyebrow")}
          </p>
          <h1 className="mt-5 font-display text-[2.75rem] font-medium leading-[1.05] tracking-[-0.025em] text-white sm:text-[3.5rem]">
            {t("login.heroTitleLine1")}
            <br />
            <span className="text-white/55">{t("login.heroTitleLine2")}</span>
          </h1>
          <p className="mt-6 text-base font-light leading-relaxed text-white/65">
            {t("login.heroSubtitle")}
          </p>

          {/* 30-second auto-playing demo loop — answers the "is this real
              software?" question on first impression. Reads /demo-loop.mp4
              if present (drop a screen recording into /app/frontend/public/),
              otherwise renders a styled placeholder card so the slot doesn't
              collapse the layout. Muted + playsInline so it autoplays in
              every modern browser. Falls through gracefully on error. */}
          <DemoLoopSlot />

          <div className="mt-7">
            <IpProtectedStrip tone="dark" />
          </div>
        </div>
        <p className="relative text-xs font-light text-white/40">
          © {new Date().getFullYear()} {t("login.footerCopyright")}
          {" · "}
          <a href="/privacy" data-testid="login-privacy-link" className="underline-offset-2 hover:text-ek-lilac hover:underline">{t("login.privacy")}</a>
        </p>
      </div>

      {/* Right — login form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <StarMark size={28} className="text-ek-purple" />
            <span className="font-display text-lg font-bold text-ek-ink">
              EKSELENS <span className="text-ek-purple">· ERIO</span>
            </span>
          </div>

          <p className="ek-eyebrow">{t("login.welcomeBack")}</p>
          <h2 className="ek-heading mt-2 text-3xl">{t("login.signInTitle")}</h2>
          <p className="mt-2 text-sm text-ek-ink/70">
            {t("login.useSeededAccount")}{" "}
            <a href="/how-it-works" data-testid="login-how-it-works-link" className="font-medium text-ek-purple hover:underline">
              {t("login.howItWorks")}
            </a>
          </p>
          <p className="mt-1 text-sm text-ek-ink/70">
            {t("login.notACustomerYet")}{" "}
            <LeadFormDialog
              kind="demo"
              source="login"
              trigger={
                <button type="button" data-testid="login-request-demo" className="font-medium text-ek-purple hover:underline">
                  {t("login.requestDemo")}
                </button>
              }
            />
          </p>
          {/* Language switcher — public surface for prospects landing
              from a French-language LinkedIn share. Persists choice. */}
          <div className="mt-3">
            <LocaleSwitcher tone="light" />
          </div>
          <div className="mt-4 lg:hidden">
            <IpProtectedStrip tone="light" />
          </div>

          {/* Enterprise trust strip — procurement signals for prospects landing here */}
          <div
            className="mt-5 flex flex-wrap gap-x-4 gap-y-2 rounded-xl border border-black/5 bg-white px-4 py-3"
            data-testid="login-trust-strip"
          >
            {[
              { icon: Scale, label: t("login.trustDora") },
              { icon: ShieldCheck, label: t("login.trustPatent") },
              { icon: Lock, label: t("login.trustSoc2") },
              { icon: Network, label: t("login.trustMfa") },
            ].map((b) => (
              <span
                key={b.label}
                data-testid={`login-trust-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-ek-ink/70"
              >
                <b.icon size={13} className="text-ek-purple" /> {b.label}
              </span>
            ))}
          </div>

          {/* Positioning + honest regulatory-alignment row (no fabricated customer logos) */}
          <p
            className="mt-3 text-[0.72rem] leading-relaxed text-ek-ink/65"
            data-testid="login-social-proof"
          >
            {t("login.socialProof")}
          </p>
          <div
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5"
            data-testid="login-alignment-row"
          >
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ek-ink/65">
              {t("login.alignedWith")}
            </span>
            {["DORA", "SOC 2", "ISO 27001", "GDPR", "NIST CSF"].map((f) => (
              <span
                key={f}
                data-testid={`login-alignment-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-ek-ink/65"
              >
                {f}
              </span>
            ))}
          </div>

          {mfaToken && (
            <form onSubmit={handleMfaVerify} className="mt-8 space-y-4" data-testid="mfa-login-form">
              <div className="rounded-xl border border-ek-purple/15 bg-ek-purple/[0.04] px-4 py-3">
                <p className="text-sm font-semibold text-ek-ink">{t("login.mfaTitle")}</p>
                <p className="mt-0.5 text-sm text-ek-ink/70">
                  {t("login.mfaSubtitle")}
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ek-ink" htmlFor="mfa-code">
                  {t("login.mfaCodeLabel")}
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  inputMode="text"
                  autoComplete="one-time-code"
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                  data-testid="mfa-code-input"
                  placeholder="123456"
                  className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-center text-lg tracking-[0.3em] text-ek-ink outline-none transition-colors focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600" data-testid="mfa-login-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="mfa-verify-button"
                className="ek-btn w-full disabled:opacity-60"
              >
                {loading ? t("login.verifying") : t("login.verifyAndSignIn")}
              </button>
              <button
                type="button"
                onClick={() => { setMfaToken(""); setMfaCode(""); setError(""); }}
                data-testid="mfa-cancel-button"
                className="w-full text-center text-sm text-ek-ink/70 hover:text-ek-purple"
              >
                {t("login.backToSignIn")}
              </button>
            </form>
          )}

          {!mfaToken && (
          <>
          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ek-ink" htmlFor="email">
                {t("login.emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                placeholder="cro@erio.test"
                className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ek-ink outline-none transition-colors focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ek-ink" htmlFor="password">
                {t("login.passwordLabel")}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                placeholder="••••••••"
                className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ek-ink outline-none transition-colors focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="ek-btn w-full disabled:opacity-60"
            >
              {loading ? t("login.signingIn") : t("login.signIn")}
            </button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-black/10" />
            <span className="text-[0.65rem] uppercase tracking-widest text-ek-ink/65">{t("login.or")}</span>
            <div className="h-px flex-1 bg-black/10" />
          </div>
          <button
            type="button"
            data-testid="google-owner-signin-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 flex w-full items-center justify-center gap-2.5 rounded-full border border-black/10 bg-white py-2.5 text-sm font-medium text-ek-ink transition-colors hover:bg-ek-purple/5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            {t("login.ownerGoogleSignIn")}
          </button>
          <p className="mt-1.5 text-center text-[0.68rem] text-ek-ink/65">
            {t("login.ownerGoogleNote")}
          </p>

          {ssoEnabled && (
            <button
              type="button"
              data-testid="sso-signin-button"
              onClick={() => { window.location.href = `${API}/auth/oidc/login`; }}
              className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-full border border-ek-purple/30 bg-ek-purple/[0.04] py-2.5 text-sm font-medium text-ek-purple transition-colors hover:bg-ek-purple/10"
            >
              <Network size={15} /> {t("login.ssoSignIn")}
            </button>
          )}

          {/* Shared-tenant SSO (EKSELENS Auth0) — renders only when Auth0 env is set */}
          <div className="mt-3">
            <EkselensSsoButton />
          </div>

          {/* Magic-link fallback — emails a single-use sign-in link.
              Helps users who forgot their password without standing up a
              full password-reset flow. Anonymous & rate-limited server-side. */}
          <button
            type="button"
            data-testid="magic-link-request"
            onClick={async () => {
              if (!email) {
                toast.message(t("login.magicLinkPrompt"));
                return;
              }
              try {
                await axios.post(`${API}/auth/magic-link/request`, { email });
                toast.success(
                  t("login.magicLinkSent"),
                  { duration: 6000 },
                );
              } catch {
                toast.error(t("login.magicLinkError"));
              }
            }}
            className="mt-3 w-full rounded-full border border-black/10 bg-white py-2 text-xs font-semibold text-ek-ink/65 transition-colors hover:border-ek-purple/40 hover:text-ek-purple"
          >
            {t("login.magicLinkButton")}
          </button>

          <p className="mt-4 text-center text-xs text-ek-ink/60">
            {t("login.dontHaveAccount")}{" "}
            <Link to="/signup" className="font-semibold text-ek-purple hover:underline"
                  data-testid="login-signup-link">
              {t("login.signUpCta")}
            </Link>
          </p>

          <div className="mt-8 rounded-xl border border-black/5 bg-white p-4">
            <p className="ek-eyebrow mb-2 text-[0.65rem]">{t("login.demoAccountsTitle")}</p>
            <div className="space-y-1">
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.email}
                  type="button"
                  onClick={() => {
                    setEmail(a.email);
                    setPassword("DemoPass!1");
                  }}
                  data-testid={`demo-account-${a.email.split("@")[0]}`}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-ek-purple/5"
                >
                  <span className="font-medium text-ek-ink">{a.email}</span>
                  <span className="text-ek-ink/65">{a.role}</span>
                </button>
              ))}
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
